import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '@/components/ui/toaster';
import { newsletterApi } from '@/lib/api/newsletter';
import { FileDown, CheckCircle2, BookOpen } from 'lucide-react';



export function LeadMagnetSection() {
    const [email, setEmail] = useState('');
    const [consent, setConsent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        if (!consent) {
            toast.error('Consenso richiesto', {
                description: 'Devi accettare la privacy policy per scaricare la guida.',
            });
            return;
        }

        setIsLoading(true);
        try {
            await newsletterApi.subscribe(email);
        } catch (error) {
            // If already subscribed, that's fine — still give the PDF
            const msg = error instanceof Error ? error.message : '';
            if (!msg.includes('già iscritta')) {
                toast.error('Errore', { description: msg || 'Riprova più tardi.' });
                setIsLoading(false);
                return;
            }
        }

        // Trigger download
        const link = document.createElement('a');
        link.href = '/AI-in-Azienda-Checklist-7-Step.pdf';
        link.download = 'AI-in-Azienda-Checklist-Operativa-7-Step-Ballerano.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setDownloaded(true);
        toast.success('Guida scaricata!', {
            description: 'Controlla la cartella download. Riceverai anche la newsletter settimanale.',
        });
        setIsLoading(false);
    };

    const benefits = [
        "Framework testato su aziende reali",
        "Checklist stampabile per ogni step",
        "Template di self-assessment incluso",
        "Errori da evitare (e come li ho visti fallire)",
    ];

    return (
        <section id="guida-gratuita" style={{
            padding: "clamp(60px, 10vw, 100px) 24px",
            background: "linear-gradient(170deg, #0F0F23 0%, #1A1A2E 50%, #16213E 100%)",
            color: "white",
            position: "relative",
            overflow: "hidden",
        }}>
            {/* Background orbs */}
            <div style={{
                position: "absolute", top: "15%", left: "5%", width: 300, height: 300,
                borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.1), transparent)",
                filter: "blur(60px)",
            }} />
            <div style={{
                position: "absolute", bottom: "10%", right: "8%", width: 200, height: 200,
                borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.08), transparent)",
                filter: "blur(50px)",
            }} />

            <div style={{
                maxWidth: 1000, margin: "0 auto", position: "relative", zIndex: 2,
                display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: "clamp(32px, 5vw, 64px)",
                alignItems: "center",
            }} className="lead-magnet-grid">

                {/* Left: content */}
                <div>
                    <div style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        padding: "5px 14px", borderRadius: 14,
                        background: "rgba(5,150,105,0.15)", border: "1px solid rgba(5,150,105,0.3)",
                        marginBottom: 20,
                    }}>
                        <FileDown size={14} color="#34D399" />
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#34D399", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                            Guida gratuita
                        </span>
                    </div>

                    <h2 style={{
                        fontFamily: "'Playfair Display', serif",
                        fontSize: "clamp(1.6rem, 3.5vw, 2.3rem)",
                        fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.02em",
                        marginBottom: 14,
                    }}>
                        AI in Azienda:<br />
                        <span style={{ color: "#93C5FD" }}>La Checklist Operativa in 7 Step</span>
                    </h2>

                    <p style={{
                        fontSize: 15, lineHeight: 1.7, color: "rgba(255,255,255,0.65)",
                        marginBottom: 24, maxWidth: 480,
                    }}>
                        Come introdurre l'intelligenza artificiale nella tua PMI senza bruciare
                        budget, tempo e motivazione del team.
                    </p>

                    <div style={{ marginBottom: 8 }}>
                        {benefits.map((b, i) => (
                            <div key={i} style={{
                                display: "flex", gap: 10, alignItems: "flex-start",
                                marginBottom: 10,
                            }}>
                                <CheckCircle2 size={16} style={{ color: "#34D399", flexShrink: 0, marginTop: 3 }} />
                                <span style={{ fontSize: 14, color: "rgba(255,255,255,0.8)", lineHeight: 1.5 }}>
                                    {b}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: form card */}
                <div style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 16,
                    padding: "clamp(24px, 4vw, 36px)",
                    backdropFilter: "blur(10px)",
                }}>
                    {!downloaded ? (
                        <>
                            <h3 style={{
                                fontSize: 18, fontWeight: 700, marginBottom: 6,
                                fontFamily: "'Playfair Display', serif",
                            }}>
                                Scarica la guida gratis
                            </h3>
                            <p style={{
                                fontSize: 13, color: "rgba(255,255,255,0.5)",
                                marginBottom: 20,
                            }}>
                                Inserisci la tua email e ricevi subito il PDF + la newsletter settimanale AI.
                            </p>

                            <form onSubmit={handleSubmit}>
                                <input
                                    type="email"
                                    placeholder="La tua email aziendale"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    style={{
                                        width: "100%",
                                        padding: "14px 16px",
                                        borderRadius: 10,
                                        border: "1px solid rgba(255,255,255,0.15)",
                                        background: "rgba(255,255,255,0.08)",
                                        color: "white",
                                        fontSize: 15,
                                        outline: "none",
                                        marginBottom: 12,
                                        boxSizing: "border-box",
                                    }}
                                />

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    style={{
                                        width: "100%",
                                        padding: "14px 24px",
                                        borderRadius: 10,
                                        border: "none",
                                        background: "linear-gradient(135deg, #3B82F6, #6366F1)",
                                        color: "white",
                                        fontWeight: 700,
                                        fontSize: 15,
                                        cursor: isLoading ? "wait" : "pointer",
                                        opacity: isLoading ? 0.7 : 1,
                                        transition: "all 0.3s ease",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 8,
                                        boxShadow: "0 4px 20px rgba(59,130,246,0.3)",
                                    }}
                                >
                                    <FileDown size={16} />
                                    {isLoading ? 'Un momento...' : 'Scarica la guida gratuita'}
                                </button>

                                <label style={{
                                    display: "flex", alignItems: "flex-start", gap: 8,
                                    cursor: "pointer", marginTop: 14,
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={consent}
                                        onChange={(e) => setConsent(e.target.checked)}
                                        style={{
                                            width: 16, height: 16, marginTop: 2,
                                            accentColor: "#3B82F6", cursor: "pointer",
                                        }}
                                    />
                                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", lineHeight: 1.5 }}>
                                        Acconsento al trattamento dei dati secondo la 
                                        <Link to="/privacy-policy" style={{ color: "#93C5FD", textDecoration: "underline" }}>
                                            Privacy Policy
                                        </Link>
                                        . Riceverai anche la newsletter settimanale. Cancellazione in qualsiasi momento.
                                    </span>
                                </label>
                            </form>
                        </>
                    ) : (
                        <div style={{ textAlign: "center", padding: "12px 0" }}>
                            <div style={{
                                width: 56, height: 56, borderRadius: "50%",
                                background: "rgba(5,150,105,0.2)",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                marginBottom: 16,
                            }}>
                                <CheckCircle2 size={28} color="#34D399" />
                            </div>
                            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
                                Guida scaricata!
                            </h3>
                            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginBottom: 20, lineHeight: 1.6 }}>
                                Controlla la cartella download. Sei anche iscritto alla newsletter.
                            </p>

                            <div style={{
                                padding: "12px 14px", borderRadius: 10,
                                background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                                marginBottom: 14, textAlign: "left",
                            }}>
                                <div style={{
                                    fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                                    textTransform: "uppercase", color: "#93C5FD", marginBottom: 8,
                                }}>
                                    Prossimi passi
                                </div>
                                <Link to="/blog"
                                    style={{
                                        display: "flex", alignItems: "center", gap: 10,
                                        padding: "10px 12px", borderRadius: 8,
                                        color: "white", textDecoration: "none", fontSize: 13.5,
                                        background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                                    }}>
                                    <BookOpen size={15} style={{ color: "#93C5FD", flexShrink: 0 }} />
                                    <span>
                                        <strong style={{ display: "block" }}>Vuoi approfondire?</strong>
                                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>Leggi gli articoli del blog</span>
                                    </span>
                                </Link>
                            </div>

                            <a
                                href="/AI-in-Azienda-Checklist-7-Step.pdf"
                                download="AI-in-Azienda-Checklist-Operativa-7-Step-Ballerano.pdf"
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 8,
                                    padding: "8px 16px", borderRadius: 8,
                                    color: "rgba(255,255,255,0.6)", textDecoration: "none",
                                    fontSize: 12, fontWeight: 500,
                                }}
                            >
                                <FileDown size={13} /> Scarica di nuovo il PDF
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
